// Singleton WebSocket client with session-cookie auth, exponential backoff
// reconnect, and tab-visibility aware idling. Messages arrive as
// { kind, payload } envelopes dispatched to every listener.

export type WsMessage = {
  kind:
    | 'hello'
    | 'job_update'
    | 'ai_job_update'
    | 'bookmark_meta_update'
    | 'bookmark_created'
    | 'bookmark_updated'
    | 'bookmark_deleted'
    | 'bookmarks_bulk_deleted'
    | 'folder_created'
    | 'folder_updated'
    | 'folder_deleted'
    | 'ai_queue_stats'
    | 'ai_dlq_added';
  payload: unknown;
};

type Listener = (msg: WsMessage) => void;

class JobsSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private explicitlyClosed = false;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.explicitlyClosed = false;
    this.connect();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibility);
    }
  }

  stop(): void {
    this.explicitlyClosed = true;
    this.started = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.ws?.close();
    this.ws = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private connect(): void {
    if (this.explicitlyClosed) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws`;
    try {
      this.ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.addEventListener('open', () => {
      this.reconnectDelay = 1000;
    });

    this.ws.addEventListener('message', (event) => {
      try {
        const parsed = JSON.parse(event.data) as WsMessage;
        for (const fn of this.listeners) fn(parsed);
      } catch {
        // Ignore non-JSON frames.
      }
    });

    this.ws.addEventListener('close', () => {
      this.ws = null;
      if (!this.explicitlyClosed) this.scheduleReconnect();
    });

    this.ws.addEventListener('error', () => {
      this.ws?.close();
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const delay = this.reconnectDelay;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      // Give the tab 60s to come back before dropping the socket.
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        this.ws?.close();
      }, 60_000);
    } else {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }
      if (!this.ws) this.connect();
    }
  };
}

export const jobsSocket = new JobsSocket();
